<template>
  <figure
    class="rating_input"
    tabindex="0"
    @mouseleave="onMouseout"
    @keyup="keyHandler"
  >
    <div
      class="rating_input__rating_blocks"
      :class="{
        'rating_input__rating_blocks--right-to-left': colorOrder === 'rtl',
      }"
      ref="optionsRail"
      v-touch:moving="touchMoveHandler"
      v-touch:end="touchEndHandler"
    >
      <RatingBlock
        class="rating_input__rating_block"
        v-for="star in starArray"
        :key="star.id"
        :setRating="star.id / (5 / pointScale)"
        :value="star.value"
        :color="star.color"
        :isCosmeticRating="isCosmeticRating"
        :allowHalves="pointScale === 10"
        :type="type"
        @onSelect="setRating"
        @onHover="setHoverRating"
        ref="options"
      >
        <span v-if="star.id === 5">☹️</span>
        <span v-if="star.id === 4">🙁</span>
        <span v-if="star.id === 3">😐</span>
        <span v-if="star.id === 2">🙂</span>
        <span v-if="star.id === 1">😄</span>
      </RatingBlock>
    </div>
    <ClearButton @clear="clearValue" v-if="!required && value" />
    <ValidationMessage
      :displayInvalid="this.isShowingValidationMessage && !this.isValid"
      :validation-message="externalValidationErrorMessage || validationMessage"
    />
  </figure>
</template>

<script>
/* eslint-disable prefer-destructuring */
import BaseComponent from "../BaseComponent";
import globalComponentSettings from "../../../global-component-settings";
import InputBase from "../atoms/Inputs/mixins/InputBase";
import RatingBlock from "../atoms/RatingBlock.vue";
import ClearButton from "../atoms/ClearButton.vue";

/**
 * @group Inputs
 * This is a rating input. The user inputs a rating here. This is our core business.
 */
export default {
  mixins: [BaseComponent, InputBase],
  name: "RatingInput",
  components: {
    RatingBlock,
    ClearButton,
  },
  props: {
    /**
     * @vuese
     * This input supports half inputs, this means that if you'd put the scale to 10
     * the user could input 1 - 10. If you put it to 5, the user would be able to
     * set the number to 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5 and 5.
     */
    pointScale: {
      type: Number,
      default: 5,
    },
    /**
     * @vuese
     * What symbols the rating input uses
     */
    type: {
      // `'stars'` / `'smileys'`
      type: String,
      validator(value) {
        return ["stars", "smileys"].indexOf(value) !== -1;
      },
      default: "stars",
    },
    /**
     * @vuese
     * The order is red - orange - green. ltr stands for left to right,
     * rtl stand for right to left. This indicates to the user if the rating they are giving
     * is good or bad and occurs when selecting or hovering.
     */
    colorOrder: {
      type: String,
      validator(value) {
        return ["ltr", "rtl"].indexOf(value) !== -1;
      },
      default: "ltr",
    },
    required: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: "Please select an option",
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
  },
  data() {
    return {
      isCosmeticRating: false,
      displayValue: this.value || 0,
      touchEventStore: {
        dragValue: undefined,
        elemenntOffsetLeft: undefined,
        closestToTouch: undefined,
      },
    };
  },
  computed: {
    color() {
      return this.getColorForRating(this.displayValue);
    },
    starArray() {
      const amountOfStars = 5;
      let starRating = this.displayValue / 2;
      if (this.pointScale === 5) {
        starRating = this.displayValue;
      }
      const starArray = [];
      let { color } = this;
      for (let index = 0; index < Math.round(amountOfStars); index += 1) {
        let add = 0;
        if (this.type === "stars") {
          if (index < starRating) add = 1;
          if (starRating - index === 0.5) add = 0.5;
        } else {
          color = this.getColorForRating(index + 1);
          if (index + 1 === this.displayValue) add = 1;
          if (index + 1 === this.value) add = 1;
        }
        starArray.push({
          color,
          value: add,
          id: index + 1,
        });
      }
      return starArray;
    },
    isValid() {
      if (this.enableExternalValidation) return this.externalValidationError;
      return (
        (this.required &&
          this.value !== null &&
          this.value !== "" &&
          this.value !== undefined) ||
        !this.required
      );
    },
  },
  watch: {
    value() {
      this.displayValue = this.value;
    },
  },
  methods: {
    getColorForRating(rating) {
      let colorForRating = "grey";
      let normalizedRating = rating;
      if (this.pointScale === 5) normalizedRating *= 2;
      let colors = ["red", "orange", "green"];
      if (this.colorOrder === "rtl") colors = colors.reverse();
      const firstColorLimit = 5;
      const secondColorLimit = 7;
      if (normalizedRating < firstColorLimit) colorForRating = colors[0];
      else if (normalizedRating < secondColorLimit) colorForRating = colors[1];
      else if (normalizedRating >= secondColorLimit) colorForRating = colors[2];
      return colorForRating;
    },
    // @vuese
    // Sets the rating
    // @arg is a Number
    setRating(rating) {
      this.isCosmeticRating = false;
      this.inputValue = rating;
      this.displayValue = this.inputValue;
    },
    setHoverRating(rating) {
      if (this.isMobile) return;
      this.isCosmeticRating = true;
      this.displayValue = rating;
    },
    onMouseout() {
      if (this.isMobile) return;
      this.displayValue = this.inputValue;
    },
    // @vuese
    clearValue() {
      this.inputValue = 0;
    },
    keyHandler(event) {
      let multiplier = 1;
      if (this.type === "stars") multiplier = 2;
      let numeric = event.key * multiplier;
      let upArrow = "ArrowRight";
      let downArrow = "ArrowLeft";
      const max = 5 * multiplier;
      if (this.colorOrder === "rtl") {
        numeric = max + 1 - numeric;
        upArrow = "ArrowLeft";
        downArrow = "ArrowRight";
      }

      if (!Number.isNaN(Number(event.key))) {
        if (numeric <= max && numeric > 0) {
          this.inputValue = numeric;
        }
      } else if (event.key === upArrow) {
        if (this.value && this.inputValue + 1 <= max) {
          this.inputValue += 1;
        } else {
          this.inputValue = 1;
        }
      } else if (event.key === downArrow) {
        if (this.value && this.inputValue - 1 > 0) {
          this.inputValue -= 1;
        } else {
          this.inputValue = max;
        }
      }
    },
    /**
     * @vuese
     * On mobile the user can drag the value around. That's what this function handles.
     * The function calculates where the rating are and where the user is dragging.
     * Then highlights the rating the user is dragging over.
     * When the user stops panning that rating is selected.
     */
    touchMoveHandler(event) {
      if (!this.isMobile || !globalComponentSettings.ratingInputTouchEnabled)
        return;
      let closest = this.touchEventStore.closestToTouch;
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        // Sometimes the touch library will fail and a mousemove will be fired.
        // Mousemoves don't have targetouches, hence .pageX is used.
        const goal = event.pageX || event.targetTouches[0].pageX;
        let setRating = 0;
        this.touchEventStore.closestToTouch = this.$refs.options.reduce(
          (previous, current) => {
            closest = previous;
            const currentDistance =
              current.$el.getBoundingClientRect().left +
              current.$el.offsetWidth / 2;
            const previousDistance =
              previous.$el.getBoundingClientRect().left +
              previous.$el.offsetWidth / 2;
            if (
              Math.abs(currentDistance - goal) <
              Math.abs(previousDistance - goal)
            ) {
              closest = current;
            }
            if (
              this.pointScale === 10 &&
              Math.abs(
                closest.$el.getBoundingClientRect().left +
                  closest.$refs.halfFirst.offsetLeft -
                  goal
              ) <
                Math.abs(
                  closest.$el.getBoundingClientRect().left +
                    closest.$refs.halfLast.offsetLeft -
                    goal
                )
            ) {
              setRating = closest.setRating - 1;
            } else {
              setRating = closest.setRating;
            }
            return closest;
          }
        );
        this.touchEventStore.dragValue = parseInt(setRating, 10);
        if (this.touchEventStore.dragValue) {
          this.inputValue = this.touchEventStore.dragValue;
        }
      });
    },
    touchEndHandler() {
      if (!this.isMobile || !globalComponentSettings.ratingInputTouchEnabled)
        return;
      if (this.touchEventStore.dragValue)
        this.inputValue = this.touchEventStore.dragValue;
      this.touchEventStore.dragValue = undefined;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";

.rating_input {
  display: inline-block;
  margin: 0px;
  width: 100%;
  max-width: 376px;
}
.rating_input__rating_blocks {
  display: inline-flex;
  width: 100%;
  max-width: 376px;
}
.rating_input__rating_blocks--right-to-left {
  flex-direction: row-reverse;
  .rating_input__rating_block ~ .rating_input__rating_block {
    margin-right: 4px;
    margin-left: 0px;
  }
}

.rating_input__rating_block {
  display: inline-block;
  outline: 0px;
  & ~ .rating_input__rating_block {
    margin-left: 4px;
  }
}
.rating_input__footer {
  width: 100%;
  display: block;
  text-align: right;
}
.rating_input__clear_button {
  display: inline-block;
  margin-top: 8px;
}
</style>
